/**
 * SignaturePad Component - HTML5 canvas digital signature
 */

import React, { useEffect, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';

interface SignaturePadComponentProps {
  onSignatureChange: (signatureData: string) => void;
  label: string;
  clearLabel: string;
  hasError?: boolean;
  clearSignature?: boolean; // Trigger to clear signature from parent
}

export const SignaturePadComponent: React.FC<SignaturePadComponentProps> = ({
  onSignatureChange,
  label,
  clearLabel,
  hasError = false,
  clearSignature = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize signature pad
    const signaturePad = new SignaturePad(canvasRef.current, {
      penColor: 'rgb(0, 0, 0)',
      minWidth: 1,
      maxWidth: 3,
      velocityFilterWeight: 0.7,
    });

    signaturePadRef.current = signaturePad;

    // Handle signature changes
    const handleEndStroke = () => {
      const isEmpty = signaturePad.isEmpty();
      setIsEmpty(isEmpty);
      if (!isEmpty) {
        const dataURL = signaturePad.toDataURL('image/png');
        onSignatureChange(dataURL);
      } else {
        onSignatureChange('');
      }
    };

    signaturePad.addEventListener('endStroke', handleEndStroke);

    // Handle window resize with debounce to prevent clearing
    const handleResize = () => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Save current signature before resize
      const currentData = signaturePad.toDataURL();

      resizeTimeoutRef.current = setTimeout(() => {
        if (canvasRef.current && signaturePadRef.current) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const canvas = canvasRef.current;

          // Store current signature
          const wasEmpty = signaturePadRef.current.isEmpty();

          // Resize canvas
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.scale(ratio, ratio);
          }

          // Restore signature if there was one
          if (!wasEmpty && currentData) {
            signaturePadRef.current.fromDataURL(currentData);
          }
        }
      }, 100); // Small delay to prevent excessive resizing
    };

    // Initial setup
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvasRef.current.width = canvasRef.current.offsetWidth * ratio;
    canvasRef.current.height = canvasRef.current.offsetHeight * ratio;
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      signaturePad.removeEventListener('endStroke', handleEndStroke);
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []); // Remove onSignatureChange dependency to prevent re-initialization

  // Clear signature when triggered by parent component
  useEffect(() => {
    if (clearSignature && signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
      onSignatureChange('');
    }
  }, [clearSignature, onSignatureChange]);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
      onSignatureChange('');
    }
  };

  return (
    <div className={`sprintry-signature-pad ${hasError ? 'sprintry-signature-pad--error' : ''}`}>
      <label className="sprintry-signature-pad__label">{label}</label>
      <div className="sprintry-signature-pad__container">
        <button
          type="button"
          className="sprintry-signature-pad__clear"
          onClick={handleClear}
          disabled={isEmpty}
        >
          {clearLabel}
        </button>
        <canvas
          ref={canvasRef}
          className="sprintry-signature-pad__canvas"
        />
      </div>
    </div>
  );
};